import {
    Component,
    Input,
    Output,
    EventEmitter,
    OnInit,
    AfterViewInit,
    OnDestroy,
    OnChanges,
    SimpleChanges,
    ChangeDetectionStrategy,
} from '@angular/core';

import {
    UntypedFormArray,
    UntypedFormGroup,
    FormBuilder,
    ReactiveFormsModule,
    Validators,
    ValidatorFn,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { PSIC_COMPONENTS } from '@pacificspecialtyinsurancecompany/psic-ng-components';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-dynamic-form',
    imports: [ReactiveFormsModule, MatFormFieldModule, MatInputModule, ...PSIC_COMPONENTS],
    templateUrl: './dynamic-form.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DynamicFormComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
    @Input() fields: any[] = [];
    @Input() parentFormArray!: UntypedFormArray;
    @Output() formLoaded = new EventEmitter<UntypedFormGroup>();

    form!: UntypedFormGroup;
    private subscriptions = new Subscription();

    constructor(private fb: FormBuilder) {}

    ngOnInit() {
        const formConfig: any = {};
        this.fields.forEach((field) => {
            formConfig[field.name] = [field.value || '', this.getValidators(field)];
        });
        this.form = this.fb.group(formConfig);

        if (this.parentFormArray) {
            this.parentFormArray.push(this.form);
        }

        this.setupDependentFields();
    }

    getRemainingCharacters(field: any): number {
        const maxLength = Number(field.maxLength || 0);
        const value = this.form?.get(field.name)?.value || '';

        return Math.max(maxLength - value.length, 0);
    }

    ngAfterViewInit() {
        this.formLoaded.emit(this.form);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.fields || changes.parentFormArray) {
            // Skip the initial change (ngOnInit will create the form), only rebuild on subsequent changes
            const isInitial =
                (changes.fields && changes.fields.firstChange) ||
                (changes.parentFormArray && changes.parentFormArray.firstChange);
            if (!isInitial) {
                // Remove previous form from parent array if present
                const oldForm = this.form;
                if (this.parentFormArray && oldForm) {
                    const idx = this.parentFormArray.controls.indexOf(oldForm);
                    if (idx > -1) this.parentFormArray.removeAt(idx);
                }
                // Tear down subscriptions
                this.subscriptions.unsubscribe();
                this.subscriptions = new Subscription();

                // Rebuild form
                const formConfig: any = {};
                this.fields.forEach((field) => {
                    formConfig[field.name] = [field.value || '', this.getValidators(field)];
                });
                this.form = this.fb.group(formConfig);
                if (this.parentFormArray) {
                    this.parentFormArray.push(this.form);
                }
                this.setupDependentFields();
                this.formLoaded.emit(this.form);
            }
        }
    }

    private setupDependentFields() {
        this.fields.forEach((field) => {
            if (field.actions?.some((action: any) => action.type === 'updateDropdownOptionsSource')) {
                const control = this.form.get(field.name);
                if (control) {
                    this.subscriptions.add(
                        control.valueChanges.subscribe((value) => {
                            this.handleDependentFieldUpdate(field, value);
                        })
                    );
                }
            }
        });
    }

    private getValidators(field: any): ValidatorFn[] {
        const validators: ValidatorFn[] = [];

        if (field.required) {
            validators.push(Validators.required);
        }

        if (field.maxLength) {
            validators.push(Validators.maxLength(Number(field.maxLength)));
        }

        if (field.minLength) {
            validators.push(Validators.minLength(Number(field.minLength)));
        }

        return validators;
    }

    private handleDependentFieldUpdate(sourceField: any, value: any) {
        const selectedOption = sourceField.options?.optionsList?.find((opt: any) => opt.value === value);

        if (selectedOption?.actions) {
            selectedOption.actions.forEach((action: any) => {
                if (action.type === 'updateDropdownOptionsTarget' && action.params?.targetFieldNames) {
                    action.params.targetFieldNames.forEach((targetName: string) => {
                        const targetField = this.fields.find((f) => f.name === targetName);
                        if (targetField) {
                            targetField.options = { optionsList: action.params.optionsList };
                            this.form.get(targetName)?.setValue('');
                        }
                    });
                }
            });
        }
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }
}
